package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.glok.collections.ObservableMap
import uk.co.nickthecoder.glok.collections.asMutableObservableMap
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kyd.appearance.AppearanceResource
import uk.co.nickthecoder.kyd.appearance.Graphic
import uk.co.nickthecoder.kyd.appearance.Pose

/**
 * A [Game] is the root of a tree structure, with each node of the tree being an [Resource].
 * All of a games resources are stored within this tree.
 *
 * I chose not to call this class `Node` to avoid confusion with Glok's `Node` class.
 *
 * If the resource is a [ParentResource] (such as [Game], [Graphic] and [Folder]), then it has child nodes.
 *
 * If a [Resource] corresponds to a file (such as an image or a sound file),
 * then it also implements the [FileResource] interface.
 */
interface Resource {

    val name: String

    val parent: ParentResource?

    val resourceType: ResourceType

    fun game(): Game

    fun path(): String

    fun findUnresolved(path: String): Resource? {
        // println("Looking for $path from ${path()}")
        if (isRelativePath(path)) {
            if (path == "..") {
                return parent
            } else if (path.startsWith("../")) {
                return parent?.findUnresolved(path.substring(3))
            } else {
                if (path.isEmpty()) {
                    return this
                } else {
                    val splitParent = pathSplitParent(path)
                    //println("  Split = $splitParent")
                    if (splitParent == null) {
                        //println("    so looking for a child called $path")
                        return findChild(path)
                    } else {
                        val child: Resource? = findUnresolved(splitParent.first)
                        //println("    so looking for ${splitParent.second} from $child")
                        if (child is ParentResource) {
                            return child.findUnresolved(splitParent.second)
                        } else {
                            return null
                        }
                    }
                }
            }
        } else {
            //println("Using absolute path but using ${path.substring(1)} from ${game()}")
            return game().findUnresolved(path.substring(1))
        }
    }

    fun find(path: String): Resource? {
        val unresolved = findUnresolved(path)
        return if (unresolved is Link) {
            unresolved.linkedResource
        } else {
            unresolved
        }
    }

    fun findAppearance(path: String): AppearanceResource? = find(path) as? AppearanceResource

    fun findChild(name: String): Resource?

    fun dangerousRename(newName: String)

}


interface FileResource : Resource {

    override val parent: ParentResource

    val filename: String

}

interface ParentResource : Resource {

    val children : ObservableMap<String, Resource>

    /**
     * Similar to [path], but it also includes a trailing path.
     * This is useful when building descendant paths as there is no special case needed for the root [Game].
     *
     * [Game.path] and [Game.pathWithTrailingSlash] both return `/`.
     *
     * For all other resource types, `[pathWithTrailingSlash] != [path]` and therefore isn't a valid path.
     * This is in contrast to filesystems. where a directory may optionally have a trailing slash.
     */
    fun pathWithTrailingSlash(): String

    fun add(child: Resource)

    fun remove(child: Resource)

    fun rename(child: Resource, newName: String)

    fun walkTree(onVisit: (Resource) -> Unit)

}


/**
 * Commonality shared by [Game], [ActResource] and all other [ParentResource] concrete types.
 */
abstract class AbstractParentOrGame : ParentResource {

    override val children = mutableMapOf<String, Resource>().asMutableObservableMap()

    override fun add(child: Resource) {
        if (children[child.name] != null) {
            throw IllegalArgumentException("Child '${child.name}' already exists")
        }
        children[child.name] = child
    }

    override fun remove(child: Resource) {
        children.remove(child.name)
    }

    override fun rename(child: Resource, newName: String) {
        if (newName == child.name) return

        if (child.parent !== this) {
            throw IllegalArgumentException("Resource is not my child")
        }

        if (children[newName] != null) {
            throw IllegalArgumentException("Child '${newName}' already exists")
        }

        val oldName = child.name

        if (child is AbstractParentResource) {
            child.name = newName
        } else {
            throw IllegalArgumentException("Child cannot be renamed")
        }

        children.remove(oldName)
        children[newName] = child
    }

    override fun findChild(name: String): Resource? = children[name]

    override fun walkTree(onVisit: (Resource) -> Unit) {
        onVisit(this)

        for (child in children.values) {
            if (child is ParentResource) {
                child.walkTree(onVisit)
            } else {
                onVisit(child)
            }
        }
    }

}

/**
 * Commonality shared by [Folder] and other [ParentResource]s.
 *
 * An [Resource] which has children (which are also [Resource]s).
 * As the name suggests, it also represents a folder on the file-system.
 * For example, if we have a game file `MyGame/MyGame.kyd`, the project has a child [Folder] with [name]="example",
 * then this represents the folder `MyGame/example`.
 *
 * Folders can of course be nested to any depth you want.
 *
 * In practice the [Game]'s tree structure of [Resource]s mirrors the filesystem's tree structure (of directories and files).
 * Most [Resource]s (such [Pose]) do not have a corresponding file or directory in our filesystem
 * (as they only contain data, and not resources such as images or sounds).
 *
 * [Resource]s of type [FileResource] have a corresponding file in the filesystem (such as [Graphic]).
 * However, [Graphic] can have children, so it extends Folder too. In most cases there is no need for a [Graphic] or
 * any of its children to contain other resources, so the directory isn't created.
 * But in the cases that the folder _is_ required, we end up with `example.png` as well as a directory called `example`.
 */

abstract class AbstractParentResource(

    final override var parent: ParentResource,
    name: String

) : AbstractParentOrGame() {

    final override var name: String = name
        internal set

    final override fun game() = parent.game()

    final override fun path() = "${parent.pathWithTrailingSlash()}$name"
    final override fun pathWithTrailingSlash() = "${parent.pathWithTrailingSlash()}$name/"

    override fun dangerousRename(newName: String) {
        name = newName
    }
}

fun isRelativePath(path: String) = ! path.startsWith("/")

fun absolutePath(path: String, relativeTo: Resource): String {
    if (isRelativePath(path)) {
        if (path.startsWith("../")) {
            val parent = relativeTo.parent
            if (parent == null) {
                log.warn("Cannot create relative path $path")
                return path
            } else {
                return absolutePath(path.substring(3), parent)
            }
        } else {
            return "${relativeTo.path()}/$path"
        }
    } else {
        return path
    }
}

fun pathName(path: String): String {
    val slash = path.lastIndexOf('/')
    return if (slash < 0) {
        path
    } else {
        path.substring(slash + 1)
    }
}

fun pathNameWithoutExtension(path: String): String {
    val name = pathName(path)
    val lastDot = name.lastIndexOf('.')
    return if (lastDot < 0) {
        name
    } else {
        name.substring(0, lastDot)
    }
}

fun pathParent(path: String): String? {
    val slash = path.lastIndexOf('/')
    return if (slash < 0) {
        null
    } else {
        if (slash == 0) {
            "/"
        } else {
            path.substring(0, slash)
        }
    }
}

fun pathExtension(path: String): String {
    val dot = path.lastIndexOf('.')
    return if (dot < 0) {
        ""
    } else {
        path.substring(dot + 1)
    }
}

fun pathSplitParent(path: String): Pair<String, String>? {
    val slash = path.lastIndexOf('/')
    return if (slash < 0) {
        null
    } else {
        Pair(path.substring(0, slash), path.substring(slash + 1))
    }
}

